import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useLead } from '../contexts/LeadContext';
import { MODELS } from '../data/models';
import { PhotoPlaceholder } from './PhotoPlaceholder';
import { Reveal } from './Reveal';
import { trackVehicleInterest } from '../tracking';
import { scrollToLeadForm } from '../utils/scroll';

/**
 * Model interest capture. Picking a card stores the model in shared state, fires
 * VehicleInterest, and pre-fills the form's vehicle field.
 *
 * Full-bleed photography with the type set over it — the photograph is the card, not
 * a thumbnail inside one. No tilt, no shadow, no border: the image is the decoration.
 *
 * The only treatment over a photo is a bottom-anchored scrim, present solely so white
 * type stays legible. No color overlay, duotone, or filter touches the image itself.
 */
export function MostWanted() {
  const {
    t
  } = useLanguage();
  const {
    vehicleInterest,
    setVehicleInterest
  } = useLead();
  const reduce = useReducedMotion();
  const choose = (id: (typeof MODELS)[number]['id'], name: string) => {
    setVehicleInterest(id);
    trackVehicleInterest(name);
    scrollToLeadForm();
  };
  return <section className="w-full bg-white" data-id="element-285" data-mp="12x8goldrk1uq">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 md:py-20" data-id="element-286" data-mp="130e8479v7ftm">
        <Reveal data-id="element-287" data-mp="17va0x9mxe77s">
          <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.02] tracking-[-0.03em] text-ink sm:text-[2.75rem]" data-id="element-288" data-mp="17yxu27ogkrlz">
            {t('wanted.title')}
          </h2>
          <div aria-hidden="true" className="mt-3 h-[3px] w-12 bg-kred" data-id="element-289" data-mp="y11jpi3ci0ox" />
          <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-body" data-id="element-290" data-mp="k8tnpsjvydra">{t('wanted.sub')}</p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10" data-id="element-291" data-mp="p6v94k4sap5x">
          <ul className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-4 md:mx-0 md:grid md:grid-cols-3 md:gap-4 md:overflow-visible md:px-0 md:pb-0" data-id="element-292" data-mp="a8uh41710xh2">
            {MODELS.map(model => {
            const selected = vehicleInterest === model.id;
            return <li key={model.id} className="w-[84vw] shrink-0 snap-center sm:w-[62vw] md:w-auto" data-id="element-293" data-mp="3nirshoj41by9">
                  <motion.button type="button" onClick={() => choose(model.id, model.name)} aria-pressed={selected} whileHover={reduce ? undefined : {
                y: -4
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 26
              }} className="group relative block aspect-[3/4] w-full overflow-hidden bg-ink text-left" data-id="element-294" data-mp="3ij5cx8ft7jma">
                    {model.photo ? <img src={model.photo} alt={model.name} loading="lazy" decoding="async" className={`h-full w-full object-cover transition-transform duration-700 ease-out ${reduce ? '' : 'group-hover:scale-[1.04]'}`} data-id="element-295" data-mp="5fp6cvnvxce" /> : <PhotoPlaceholder label={t('wanted.photo', {
                  model: model.name.toUpperCase()
                })} tone="dark" data-id="element-296" data-mp="15dgwthqm8y0h" />}

                    {/* Legibility scrim only — bottom-anchored, no tint across the image. */}
                    <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-ink via-ink/70 to-transparent" data-id="element-297" data-mp="1k0tlhfbpzjv3" />

                    {/* Selected state reads as a red rule along the base, never a fill. */}
                    {selected && <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1 bg-kred" data-id="element-298" data-mp="1xz0ae5yoy9im" />}

                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6" data-id="element-299" data-mp="3n3ikbwc71n1">
                      <p className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-gold" data-id="element-300" data-mp="3p6c35wp7cwi">
                        {t(model.line)}
                      </p>
                      <h3 className="mt-2 font-display text-2xl font-extrabold uppercase leading-none tracking-[-0.02em] text-white sm:text-3xl" data-id="element-301" data-mp="1p5487g6khq8d">
                        {model.name}
                      </h3>
                      <span className={`mt-4 inline-block text-[11px] font-bold uppercase tracking-[0.16em] ${selected ? 'text-kred' : 'text-white/75'}`} data-id="element-302" data-mp="1z1fi5zioq3zn">
                        {t('wanted.cta')}
                      </span>
                    </div>
                  </motion.button>
                </li>;
          })}
          </ul>
          <p className="mt-3 text-xs text-muted md:hidden" data-id="element-303" data-mp="rp6sm6pbr9cl">{t('wanted.swipe')}</p>
        </Reveal>
      </div>
    </section>;
}